/**
 * nw-tab-scroller.js - 标签页滚动容器组件
 * 
 * 使用方式（包装现有 HTML 结构）：
 * <nw-tab-scroller>
 *   <div class="relative">
 *     <div class="tab-scroll-container ...">
 *       <span class="tab-arrow-left ...">...</span>
 *       <span class="tab-item active">Tab 1</span>
 *       <span class="tab-item">Tab 2</span>
 *       <span class="tab-arrow-right ...">...</span>
 *     </div>
 *   </div>
 * </nw-tab-scroller>
 * 
 * 组件会自动：
 * - 检测容器是否可滚动
 * - 显示/隐藏左右箭头
 * - 点击箭头时激活相邻标签并滚动到可视区域
 */

(function () {
  if (customElements.get('nw-tab-scroller')) return;

  class NwTabScroller extends HTMLElement {
    constructor() {
      super();
      this.tabScrollContainer = null;
      this.tabArrowLeft = null;
      this.tabArrowRight = null;
      this.tabItems = [];
      this.resizeTimer = null;
      this.observer = null;
      this.handleResizeBound = this.handleResize.bind(this);
      this.isInitialized = false;
      this.handleLeftClick = null;
      this.handleRightClick = null;
      this.abortController = null;
    }

    connectedCallback() {
      // 防止重复初始化
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      
      // 使用 requestAnimationFrame 确保 DOM 已完全渲染
      // 对于动态生成的元素，可能需要多次尝试
      this.tryInit();
    }

    tryInit(retryCount = 0) {
      const maxRetries = 5;
      const retryDelay = 100;
      
      requestAnimationFrame(() => {
        // 检查必要元素是否存在
        const hasRequiredElements = 
          this.querySelector('.tab-scroll-container') &&
          this.querySelector('.tab-arrow-left') &&
          this.querySelector('.tab-arrow-right') &&
          this.querySelectorAll('.tab-item').length > 0;
        
        if (hasRequiredElements) {
          this.init();
        } else if (retryCount < maxRetries) {
          // 如果元素还没准备好，延迟重试
          setTimeout(() => {
            this.tryInit(retryCount + 1);
          }, retryDelay);
        }
      });
    }

    disconnectedCallback() {
      this.cleanup();
    }

    init() {
      // 防止重复初始化
      if (this.isInitialized) return;
      
      this.tabScrollContainer = this.querySelector('.tab-scroll-container');
      this.tabArrowLeft = this.querySelector('.tab-arrow-left');
      this.tabArrowRight = this.querySelector('.tab-arrow-right');
      this.tabItems = Array.from(this.querySelectorAll('.tab-item'));

      if (!this.tabScrollContainer || !this.tabArrowLeft || !this.tabArrowRight || this.tabItems.length === 0) {
        return;
      }

      // 清理旧的事件监听器
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      // 绑定箭头点击事件，使用 stopPropagation 防止事件冒泡
      this.handleLeftClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.handleArrowClick('left');
      };
      this.handleRightClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        this.handleArrowClick('right');
      };
      
      this.tabArrowLeft.addEventListener('click', this.handleLeftClick, { signal });
      this.tabArrowRight.addEventListener('click', this.handleRightClick, { signal });

      // 监听标签激活变化
      this.observer = new MutationObserver(() => {
        // 延迟一下确保 class 已更新
        requestAnimationFrame(() => {
          this.updateArrowVisibility();
        });
      });
      this.tabItems.forEach(item => {
        this.observer.observe(item, { attributes: true, attributeFilter: ['class'] });
        
        // 监听 tab 的直接点击事件，确保外部点击时也能更新箭头
        item.addEventListener('click', () => {
          requestAnimationFrame(() => {
            this.updateArrowVisibility();
          });
        });
      });

      // 监听窗口大小变化
      window.addEventListener('resize', this.handleResizeBound);

      // 标记已初始化
      this.isInitialized = true;

      // 初始更新
      this.updateArrowVisibility();
    }

    cleanup() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = null;
      }
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      window.removeEventListener('resize', this.handleResizeBound);
      this.isInitialized = false;
      this.handleLeftClick = null;
      this.handleRightClick = null;
    }

    handleResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer);
      }
      this.resizeTimer = setTimeout(() => {
        this.updateArrowVisibility();
      }, 100);
    }

    isScrollable() {
      if (!this.tabScrollContainer) return false;
      return this.tabScrollContainer.scrollWidth > this.tabScrollContainer.clientWidth;
    }

    updateArrowVisibility() {
      
      if (!this.tabArrowLeft || !this.tabArrowRight) return;

      if (!this.isScrollable()) {
        this.tabArrowLeft.classList.add('tw-hidden');
        this.tabArrowRight.classList.add('tw-hidden');
        return;
      }

      // 重新获取 tabItems，防止动态添加/删除
      this.tabItems = Array.from(this.querySelectorAll('.tab-item'));
      
      const activeIndex = this.tabItems.findIndex(item => item.classList.contains('active'));
      
      // 不是第一个就显示左箭头，不是最后一个就显示右箭头
      const isFirst = activeIndex <= 0;
      const isLast = activeIndex >= this.tabItems.length - 1;
      
      this.tabArrowLeft.classList.toggle('tw-hidden', isFirst);
      this.tabArrowRight.classList.toggle('tw-hidden', isLast);
    }

    handleArrowClick(direction) {
      // 如果还没初始化，尝试初始化
      if (!this.isInitialized) {
        this.tryInit();
        // 等待初始化完成
        setTimeout(() => {
          this.handleArrowClick(direction);
        }, 150);
        return;
      }
      
      if (!this.isScrollable()) return;

      // 重新获取 tabItems，确保是最新的
      this.tabItems = Array.from(this.querySelectorAll('.tab-item'));
      const activeIndex = this.tabItems.findIndex(item => item.classList.contains('active'));
      let targetIndex = -1;

      if (direction === 'left' && activeIndex > 0) {
        targetIndex = activeIndex - 1;
      } else if (direction === 'right' && activeIndex < this.tabItems.length - 1) {
        targetIndex = activeIndex + 1;
      }

      if (targetIndex === -1) return;

      const targetTab = this.tabItems[targetIndex];

      // 触发点击事件，让外部逻辑处理激活
      targetTab.click();

      // 滚动到可视区域
      requestAnimationFrame(() => {
        const targetOffsetLeft = targetTab.offsetLeft;
        const containerWidth = this.tabScrollContainer.clientWidth;
        const targetWidth = targetTab.offsetWidth;
        const scrollTo = targetOffsetLeft - (containerWidth / 2) + (targetWidth / 2);
        this.tabScrollContainer.scrollTo({ left: scrollTo, behavior: 'smooth' });
        
        // 点击后验证并更新箭头可见性
        setTimeout(() => {
          this.updateArrowVisibility();
        }, 100);
      });
    }
  }

  customElements.define('nw-tab-scroller', NwTabScroller);
})();

